import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import {
  ReactFlow,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ConnectionMode,
} from '@xyflow/react'
import type { Node, Edge } from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import BackgroundPattern from '../modules/BackgroundPattern'
import CanvasActions from './CanvasActions'
import WorkflowActions from './WorkflowActions'
import AddNodePanelContent from './dialogs/AddNodePanelContent'
import { WorkflowSaveDialog } from './dialogs/WorkflowSaveDialog'
import { WorkflowLoadDialog } from './dialogs/WorkflowLoadDialog'
import { useTags } from '@/hooks/useTags'
import { createTag, type Tag } from '@/lib/tagsApi'
import TriggerNode from '../nodes/TriggerNode'
import ActionNode from '../nodes/ActionNode'
import SubFlowNode from '../nodes/SubFlowNode'
import StickyNote, { type StickyNoteData } from '../nodes/StickyNote'
import StickyNoteInspector from './inspectors/StickyNoteInspector'
import SubFlowInspector from './inspectors/SubFlowInspector'
import NodePropertiesPanel from './inspectors/NodePropertiesPanel'
import EdgePropertiesPanel from './inspectors/EdgePropertiesPanel'
import FloatingPanel from '../modules/FloatingPanel'
import { Badge } from '@lineage/ui'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { X, Activity, Eye, EyeOff, Clock, Cpu, Database, Plus, Minus, Hand, Map } from 'lucide-react'
import EmptyStateCTA from './EmptyStateCTA'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import { useOnConnect } from '../hooks/useOnConnect'
import { useDnDNodes } from '../hooks/useDnDNodes'
import { useSubFlows } from '../hooks/useSubFlows'
import { useNodeDragDrop } from '../hooks/useNodeDragDrop'
import { useVerticalHandlesInSubflows } from '../hooks/useVerticalHandlesInSubflows'
import { useHelperLines } from '../hooks/useHelperLines'
import { useWorkflow, DEFAULT_VIEWPORT } from '../hooks/useWorkflow'
import type { WorkflowMetadata, WorkflowViewport } from '../hooks/useWorkflow'
import { HelperLines } from './HelperLines'
import AutoHideMiniMap from '@/components/ui/AutoHideMiniMap'
import { getChildNodes, findGroupAtPoint } from '@/lib/subflow-utils'
import ViewportLogger from './devtools/ViewportLogger'
import NodeInspector from './devtools/NodeInspector'
import { withChangeLogger } from './devtools/ChangeLogger'
import { absoluteToRelativePositionDeep, relativeToAbsolutePositionDeep } from '@/lib/subflow-utils'
import { NODE_COLOR_OPTIONS, type NodeColor } from '@/lib/node-colors'
import { useUiFlags } from '@/contexts/UiFlagsContext'
import { Toggle } from '@lineage/ui'
import { useToast } from '@/hooks/use-toast'
import { ComplianceToolbar } from './canvas/ComplianceToolbar'

type BgVariant = 'none' | 'dots' | 'lines' | 'cross'
type EdgeKind = 'default' | 'smoothstep' | 'step' | 'straight' | 'bezier'

const nodeTypes = { trigger: TriggerNode, action: ActionNode, subflow: SubFlowNode, sticky: StickyNote }

export interface CanvasPaneProps {
  bgVariant: BgVariant
  edgeKind: EdgeKind
  showGrid: boolean
  showDevtools: boolean
}

export default function CanvasPane({ bgVariant, edgeKind, showGrid, showDevtools }: CanvasPaneProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [spacePan, setSpacePan] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedStickyNote, setSelectedStickyNote] = useState<Node | null>(null)
  const [selectedSubFlow, setSelectedSubFlow] = useState<Node | null>(null)
  const [selectedNode, setSelectedNode] = useState<Node | null>(null)
  const [selectedNodeType, setSelectedNodeType] = useState<'trigger' | 'action' | null>(null)
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null)
  const [activeDropGroupId, setActiveDropGroupId] = useState<string | null>(null)
  const [zoomPercent, setZoomPercent] = useState<number>(90) // Default 90% zoom
  const rf = useReactFlow()
  const { showTrace, setShowTrace } = useUiFlags()
  const { toast } = useToast()
  const [minimapReady, setMinimapReady] = useState<boolean>(false)
  const [canvasReady, setCanvasReady] = useState<boolean>(false)

  // Perf DevTools state
  const [perfVisible, setPerfVisible] = useState(false)
  const [fps, setFps] = useState(0)
  const [renderTime, setRenderTime] = useState(0)
  const [memoryUsage, setMemoryUsage] = useState<number | null>(null)
  const frameCountRef = useRef(0)
  const lastTimeRef = useRef(performance.now())
  const renderStartRef = useRef(0)

  // Minimap visibility state with localStorage persistence
  const [minimapVisible, setMinimapVisible] = useState<boolean>(() => {
    const stored = localStorage.getItem('canvas:minimap-visible')
    return stored !== null ? stored === 'true' : true
  })

  // Minimap minimized state with localStorage persistence
  const [minimapMinimized, setMinimapMinimized] = useState<boolean>(() => {
    const stored = localStorage.getItem('canvas:minimap-minimized')
    return stored !== null ? stored === 'true' : false
  })

  // Workflow persistence states
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [loadDialogOpen, setLoadDialogOpen] = useState(false)

  // Workflow hook with auto-save
  const workflow = useWorkflow({
    autoSaveInterval: 600000, // 10 minutes
    onSaveSuccess: (wf) => {
      // After save, sync ReactFlow node IDs with backend UUIDs.
      // New nodes from drag-and-drop have temporary IDs (e.g. "trigger-a1b2c")
      // that the backend replaces with real UUIDs. Without this sync,
      // features like Privacy Governance that check isValidUUID(nodeId) won't work.
      if (!wf.nodes || wf.nodes.length === 0) return
      const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      const currentNodes = rf.getNodes()
      const idMap = new Map<string, string>()

      for (const cn of currentNodes) {
        if (uuidRe.test(cn.id)) continue // already a UUID, backend preserves it
        // Match by label + position to find the backend UUID for this node
        const match = wf.nodes.find((bn: any) =>
          bn.data?.label === cn.data?.label &&
          Math.abs((bn.position?.x ?? 0) - (cn.position?.x ?? 0)) < 2 &&
          Math.abs((bn.position?.y ?? 0) - (cn.position?.y ?? 0)) < 2
        )
        if (match && match.id !== cn.id) {
          idMap.set(cn.id, match.id)
        }
      }

      if (idMap.size === 0) return

      // Update node IDs while preserving all ReactFlow internal state
      setNodes(ns => ns.map(n => {
        const newId = idMap.get(n.id)
        return newId ? { ...n, id: newId } : n
      }))

      // Update edge source/target references to new UUIDs
      setEdges(es => es.map(e => ({
        ...e,
        source: idMap.get(e.source) || e.source,
        target: idMap.get(e.target) || e.target,
      })))

      // Update selected node reference if its ID changed
      setSelectedNode(prev => {
        if (!prev) return null
        const newId = idMap.get(prev.id)
        return newId ? { ...prev, id: newId } : prev
      })
    },
    onSaveError: (error) => {
      console.error('Error saving workflow:', error)
    },
    onLoadSuccess: (wf) => {
      // Load nodes and edges from workflow
      if (wf.nodes) {
        setNodes(wf.nodes)
      }
      if (wf.edges) {
        setEdges(wf.edges)
      }
      // Restore saved viewport or use default (90% zoom)
      // Use setViewport to respect saved zoom - NEVER use fitView which auto-calculates zoom
      setTimeout(() => {
        try {
          const viewport = wf.viewport || DEFAULT_VIEWPORT
          rf.setViewport(viewport, { duration: 200 })
          // Update zoom display
          setZoomPercent(Math.round(viewport.zoom * 100))
        } catch (e) {
          console.warn('setViewport after load failed:', e)
        }
      }, 80)
    },
    onLoadError: (error) => {
      console.error('Error loading workflow:', error)
    }
  })

  // Tags for workflow management
  const { tags: availableTags, isLoading: tagsLoading, refresh: refreshTags } = useTags({})

  // Local state for pending name (n8n-style: allows editing name before saving)
  const [pendingName, setPendingName] = useState<string>('Nuevo Flujo Visual')

  // Local state for pending tags (n8n-style: allows adding tags before saving)
  const [pendingTags, setPendingTags] = useState<Array<{ id: string; name: string; color?: string }>>([])

  // Sync pending name when workflow is loaded
  useEffect(() => {
    if (workflow.currentWorkflow?.name) {
      setPendingName(workflow.currentWorkflow.name)
    } else if (!workflow.currentWorkflow) {
      // Reset to default name when no workflow (new canvas)
      setPendingName('Nuevo Flujo Visual')
    }
  }, [workflow.currentWorkflow?.id, workflow.currentWorkflow?.name])

  // Sync pending tags when workflow is loaded or tags are updated from server
  useEffect(() => {
    if (workflow.currentWorkflow?.tags) {
      setPendingTags(workflow.currentWorkflow.tags.map(t => ({
        id: t.id,
        name: t.name,
        color: t.color,
      })))
    } else if (!workflow.currentWorkflow) {
      // Clear pending tags when no workflow (new canvas)
      setPendingTags([])
    }
  }, [workflow.currentWorkflow?.id, workflow.currentWorkflow?.tags])

  // Dynamically adjust handles orientation for nodes inside subflows
  useVerticalHandlesInSubflows(nodes)

  // Keyboard shortcuts: zoom and fit handled by hook; local effect only for spacebar pan toggle
  useKeyboardShortcuts([])
  useEffect(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpacePan(true)
      }
    }
    const ku = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpacePan(false)
      }
    }
    window.addEventListener('keydown', kd)
    window.addEventListener('keyup', ku)
    return () => {
      window.removeEventListener('keydown', kd)
      window.removeEventListener('keyup', ku)
    }
  }, [])

  // Toggle Perf DevTools with Shift+D when devtools are enabled
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'D') {
        e.preventDefault()
        setPerfVisible((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // FPS counter when panel visible
  useEffect(() => {
    if (!perfVisible || !showDevtools) return
    let raf = 0
    const measure = () => {
      frameCountRef.current++
      const now = performance.now()
      const elapsed = now - lastTimeRef.current
      if (elapsed >= 1000) {
        setFps(Math.round((frameCountRef.current * 1000) / elapsed))
        frameCountRef.current = 0
        lastTimeRef.current = now
      }
      raf = requestAnimationFrame(measure)
    }
    raf = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(raf)
  }, [perfVisible, showDevtools])

  // Render time measurement
  useEffect(() => {
    renderStartRef.current = performance.now()
  }, [nodes, edges])
  useEffect(() => {
    const elapsed = performance.now() - renderStartRef.current
    setRenderTime(elapsed)
  }, [nodes, edges])

  // Memory usage (Chrome only)
  useEffect(() => {
    if (!perfVisible || !showDevtools) return
    const update = () => {
      // @ts-ignore
      if (performance.memory) {
        // @ts-ignore
        setMemoryUsage(performance.memory.usedJSHeapSize / (1024 * 1024))
      }
    }
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [perfVisible, showDevtools])

  // Keep cursor in sync with pan mode regardless of how it's toggled (keyboard or button)
  useEffect(() => {
    ;(document.body.style as any).cursor = spacePan ? 'grab' : ''
    return () => {
      ;(document.body.style as any).cursor = ''
    }
  }, [spacePan])

  // Update zoom percentage using absolute zoom value (zoom = 1.0 = 100%)
  const updateZoomPercent = useCallback(() => {
    const viewport = rf.getViewport()
    const z = viewport?.zoom
    if (!Number.isFinite(z)) return
    // Use absolute zoom: zoom = 1.0 means 100%
    setZoomPercent(Math.round(z * 100))
  }, [rf])

  // NOTE: We intentionally DO NOT call fitView on initial mount
  // The default viewport (90% zoom) or saved viewport should be respected
  // Users can manually fit view using the Controls button if needed

  // Delay MiniMap mount until after initial layout to avoid NaN viewBox/path
  // Use setTimeout instead of RAF to ensure React Flow is fully initialized
  useEffect(() => {
    const t1 = setTimeout(() => setMinimapReady(true), 300)
    const t2 = setTimeout(() => setCanvasReady(true), 100)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [])

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    rf.zoomIn({ duration: 200 })
  }, [rf])

  const handleZoomOut = useCallback(() => {
    rf.zoomOut({ duration: 200 })
  }, [rf])

  const handleTogglePan = useCallback(() => {
    setSpacePan(prev => !prev)
  }, [])

  const handleToggleMinimap = useCallback(() => {
    setMinimapVisible(prev => {
      const newValue = !prev
      localStorage.setItem('canvas:minimap-visible', String(newValue))
      return newValue
    })
  }, [])

  const handleToggleMinimapMinimized = useCallback(() => {
    setMinimapMinimized(prev => {
      const newValue = !prev
      localStorage.setItem('canvas:minimap-minimized', String(newValue))
      return newValue
    })
  }, [])

  // Workflow handlers - Microsoft Word inspired pattern
  // Quick Save (Ctrl+S):
  // - If workflow exists → saves immediately
  // - If new workflow with custom name → creates directly (Word-style)
  // - If new workflow with default name → opens dialog
  const handleQuickSave = useCallback(async () => {
    if (workflow.currentWorkflow) {
      // Existing workflow - save immediately without dialog
      const currentViewport = rf.getViewport()
      try {
        await workflow.saveWorkflow(
          {
            // Use pendingName (n8n-style: includes name changes before save)
            name: pendingName,
            description: workflow.currentWorkflow.description,
            status: workflow.currentWorkflow.status as 'draft' | 'active' | 'archived',
            visibility: workflow.currentWorkflow.visibility as 'private' | 'team' | 'organization',
            // Use pending tags (n8n-style: includes tags added before save)
            tags: pendingTags.map((tag) => tag.name),
          },
          nodes,
          edges,
          currentViewport as WorkflowViewport
        )
        toast({
          title: '✓ Flujo visual guardado',
          description: `"${pendingName}" actualizado correctamente`,
          duration: 3000,
        })
      } catch (error) {
        toast({
          title: 'Error al guardar',
          description: 'No se pudo guardar el flujo visual',
          variant: 'destructive',
        })
      }
    } else {
      // New workflow - check if user has already set a custom name
      const hasCustomName = pendingName && pendingName.trim() !== '' && pendingName !== 'Nuevo Flujo Visual' && pendingName !== 'Nuevo flujo visual'

      if (hasCustomName) {
        // User already named it - create directly without dialog (Word-style)
        const currentViewport = rf.getViewport()
        try {
          await workflow.saveWorkflow(
            {
              name: pendingName,
              description: '',
              status: 'draft',
              visibility: 'private',
              tags: pendingTags.map((tag) => tag.name),
            },
            nodes,
            edges,
            currentViewport as WorkflowViewport
          )
          toast({
            title: '✓ Flujo visual creado',
            description: `"${pendingName}" guardado correctamente`,
            duration: 3000,
          })
        } catch (error) {
          toast({
            title: 'Error al crear',
            description: 'No se pudo crear el flujo visual',
            variant: 'destructive',
          })
        }
      } else {
        // No custom name yet - open dialog to configure
        setSaveDialogOpen(true)
      }
    }
  }, [workflow, nodes, edges, rf, pendingName, pendingTags, toast])

  // Save As (Ctrl+Shift+S): Always opens dialog to save with new name/create copy
  const handleSaveAs = useCallback(() => {
    setSaveDialogOpen(true)
  }, [])

  // New Workflow (Ctrl+N): Clear canvas and start fresh
  const handleNewWorkflow = useCallback(() => {
    if (workflow.hasUnsavedChanges) {
      // TODO: Add confirmation dialog for unsaved changes
      const confirmed = window.confirm('Tienes cambios sin guardar. ¿Deseas crear un nuevo flujo visual?')
      if (!confirmed) return
    }
    setNodes([])
    setEdges([])
    workflow.resetWorkflow()
    // Reset pending states for new workflow
    setPendingName('Nuevo Flujo Visual')
    setPendingTags([])
    rf.setViewport(DEFAULT_VIEWPORT, { duration: 200 })
    setZoomPercent(Math.round(DEFAULT_VIEWPORT.zoom * 100))
  }, [workflow, setNodes, setEdges, rf])

  const handleLoadWorkflow = useCallback(() => {
    setLoadDialogOpen(true)
  }, [])

  const handleExportWorkflow = useCallback(async () => {
    if (workflow.currentWorkflow) {
      await workflow.exportWorkflow(workflow.currentWorkflow.id)
    }
  }, [workflow])

  // Inline name change handler - n8n style (local state, persisted on save)
  const handleNameChange = useCallback((newName: string) => {
    if (newName === pendingName) return

    // Update local pending name (will be saved with workflow)
    setPendingName(newName)

    // Mark as having unsaved changes
    workflow.markAsChanged()
  }, [pendingName, workflow])

  // Tag management handlers - n8n style inline editing (works before saving!)
  const handleTagAdd = useCallback((tag: Tag) => {
    // Check if tag is already added
    if (pendingTags.some(t => t.id === tag.id)) return

    // Add to local pending tags
    setPendingTags(prev => [...prev, { id: tag.id, name: tag.name, color: tag.color }])

    // Mark as having unsaved changes
    workflow.markAsChanged()
  }, [pendingTags, workflow])

  const handleTagRemove = useCallback((tagId: string) => {
    // Remove from local pending tags
    setPendingTags(prev => prev.filter(t => t.id !== tagId))

    // Mark as having unsaved changes
    workflow.markAsChanged()
  }, [workflow])

  const handleTagCreate = useCallback(async (tagName: string): Promise<Tag | null> => {
    try {
      const newTag = await createTag({ name: tagName })
      refreshTags() // Refresh the available tags list
      return newTag
    } catch (error) {
      console.error('Error creating tag:', error)
      return null
    }
  }, [refreshTags])

  const handleSaveDialogSubmit = useCallback(async (metadata: WorkflowMetadata) => {
    // Get current viewport to persist zoom and pan position
    const currentViewport = rf.getViewport()
    await workflow.saveWorkflow(metadata, nodes, edges, currentViewport as WorkflowViewport)
  }, [workflow, nodes, edges, rf])

  const handleLoadDialogSubmit = useCallback(async (workflowId: string) => {
    await workflow.loadWorkflow(workflowId)
  }, [workflow])

  // Mark workflow as changed when nodes or edges change
  useEffect(() => {
    if (workflow.currentWorkflow && (nodes.length > 0 || edges.length > 0)) {
      workflow.markAsChanged()
    }
  }, [nodes, edges])

  // Auto-save effect - include current viewport
  useEffect(() => {
    const currentViewport = rf.getViewport()
    return workflow.setupAutoSave(nodes, edges, currentViewport as WorkflowViewport)
  }, [workflow, nodes, edges, rf])

  // Keyboard shortcuts - Microsoft Word inspired
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S / Cmd+S - Quick Save
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && !e.shiftKey) {
        e.preventDefault()
        handleQuickSave()
      }
      // Ctrl+Shift+S / Cmd+Shift+S - Save As
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault()
        handleSaveAs()
      }
      // Ctrl+N / Cmd+N - New Workflow
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault()
        handleNewWorkflow()
      }
      // Ctrl+O / Cmd+O - Open Workflow
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault()
        handleLoadWorkflow()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleQuickSave, handleSaveAs, handleNewWorkflow, handleLoadWorkflow])

  // Sub-flows management
  const subFlows = useSubFlows({ nodes, edges, setNodes, setEdges })

  // Helper lines for node alignment
  const helperLines = useHelperLines()

  // Initial auto-resize for all subflows after first layout
  useEffect(() => {
    const t = setTimeout(() => {
      const groupIds = nodes.filter(n => n.type === 'subflow').map(n => n.id)
      groupIds.forEach(id => subFlows.autoResizeGroup(id))
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Normalización: asegurar que todo hijo de un SubFlow tenga extent='parent'
  // y, si su posición fue guardada como absoluta en estados legacy, convertirla a relativa.
  useEffect(() => {
    // Ejecutar una sola vez en montaje inicial (y cuando aún no hay nodos no hace nada)
    // Usamos RAF para esperar a que React Flow calcule dimensiones iniciales.
    const r = requestAnimationFrame(() => {
      setNodes((nds) => {
        if (!nds || nds.length === 0) return nds
        let changed = false

        const getParentAbsRect = (parentId: string) => {
          const parent = nds.find((n) => n.id === parentId)
          if (!parent) return null
          const absPos = relativeToAbsolutePositionDeep({ x: 0, y: 0 }, parentId, nds)
          const width = typeof parent.width === 'number'
            ? parent.width
            : (typeof parent.style?.width === 'number' ? (parent.style!.width as number) : 260)
          const height = typeof parent.height === 'number'
            ? parent.height
            : (typeof parent.style?.height === 'number' ? (parent.style!.height as number) : 160)
          return { x: absPos.x, y: absPos.y, width, height }
        }

        const isInside = (abs: { x: number; y: number }, rect: { x: number; y: number; width: number; height: number } | null) => {
          if (!rect) return false
          return (
            abs.x >= rect.x &&
            abs.y >= rect.y &&
            abs.x <= rect.x + rect.width &&
            abs.y <= rect.y + rect.height
          )
        }

        const next = nds.map((n) => {
          if (!n.parentId) return n
          let updated = { ...n }
          if (updated.extent !== 'parent') {
            updated.extent = 'parent'
            changed = true
          }

          const rect = getParentAbsRect(updated.parentId!)

          // Heurística: si tratar la posición actual como relativa lo deja fuera del padre
          // pero tratarla como absoluta lo deja dentro, entonces convertimos a relativa.
          const absIfRelative = relativeToAbsolutePositionDeep(updated.position, updated.parentId!, nds)
          const insideAsRelative = isInside(absIfRelative, rect)
          const insideAsAbsolute = isInside(updated.position, rect)

          if (!insideAsRelative && insideAsAbsolute) {
            const rel = absoluteToRelativePositionDeep(updated.position, updated.parentId!, nds)
            if (rel.x !== updated.position.x || rel.y !== updated.position.y) {
              updated.position = rel
              changed = true
            }
          }

          return updated
        })

        return changed ? next : nds
      })
    })
    return () => cancelAnimationFrame(r)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-resize affected subflows when children change
  const autoResizeAffectedGroups = useCallback((changedNodes: Node[]) => {
    // Find all unique parent IDs from changed nodes
    const affectedParentIds = new Set<string>()
    changedNodes.forEach((node) => {
      if (node.parentId) {
        affectedParentIds.add(node.parentId)
      }
    })

    // Auto-resize each affected parent
    affectedParentIds.forEach((parentId) => {
      subFlows.autoResizeGroup(parentId)
    })
  }, [subFlows])

  // Drag & drop for grouping with auto-resize
  const { onNodeDrag: onNodeDragGrouping, onNodeDragStop: originalOnNodeDragStop } = useNodeDragDrop({
    nodes,
    moveNodeToGroup: subFlows.moveNodeToGroup,
    onActiveGroupChange: setActiveDropGroupId,
    onAttachedToGroup: (groupId: string) => {
      // trigger a temporary flash on the parent node when a child is attached
      setNodes((nds) => nds.map((n) => n.id === groupId ? { ...n, data: { ...n.data, flashAttached: true } } : n))
      setTimeout(() => {
        setNodes((nds) => nds.map((n) => n.id === groupId ? { ...n, data: { ...n.data, flashAttached: false } } : n))
      }, 800)
    },
  })

  // Combined onNodeDrag handler for both grouping and helper lines
  const onNodeDrag = useCallback(
    (event: any, draggedNode: Node, draggedNodes: Node[]) => {
      // Handle parent-child grouping logic
      onNodeDragGrouping(event, draggedNode, draggedNodes)

      // Handle helper lines
      helperLines.onNodeDrag(draggedNode, nodes)

      // Constrain child movement within parent with inner padding
      if (draggedNodes.length === 1 && draggedNode.parentId) {
        const parent = nodes.find((n) => n.id === draggedNode.parentId)
        if (parent) {
          const padding = 16
          const pW = (parent.style?.width as number) ?? (parent.width as number) ?? 0
          const pH = (parent.style?.height as number) ?? (parent.height as number) ?? 0
          const cW = (draggedNode.style?.width as number) ?? (draggedNode.width as number) ?? 0
          const cH = (draggedNode.style?.height as number) ?? (draggedNode.height as number) ?? 0

          if (pW > 0 && pH > 0 && cW > 0 && cH > 0) {
            const minX = padding
            const minY = padding
            const maxX = Math.max(minX, pW - cW - padding)
            const maxY = Math.max(minY, pH - cH - padding)

            const x = (draggedNode.position as any)?.x ?? 0
            const y = (draggedNode.position as any)?.y ?? 0
            const nx = Math.min(Math.max(x, minX), maxX)
            const ny = Math.min(Math.max(y, minY), maxY)

            if (nx !== x || ny !== y) {
              setNodes((ns) => ns.map((n) => (n.id === draggedNode.id ? { ...n, position: { x: nx, y: ny } } : n)))
            }
          }
        }
      }
    },
    [onNodeDragGrouping, helperLines, nodes]
  )

  // Enhanced onNodeDragStop with auto-resize and cleanup
  const onNodeDragStop = useCallback(
    (event: any, node: Node, draggedNodes: Node[]) => {
      originalOnNodeDragStop(event, node, draggedNodes)

      // Clear the active drop group highlight
      setActiveDropGroupId(null)

      // Clear helper lines
      helperLines.onNodeDragStop()

      // Auto-resize the parent if this node has one
      if (node.parentId) {
        setTimeout(() => {
          subFlows.autoResizeGroup(node.parentId!)
        }, 0)
      }
    },
    [originalOnNodeDragStop, subFlows, helperLines]
  )

  // Connection validation
  const onConnect = useOnConnect(nodes, setEdges, edgeKind)

  const { containerRef, onDrop, addAtCenter } = useDnDNodes(setNodes)

  const handleSelectFromDialog = useCallback(
    (option: import('../data/nodeOptions').NodeOption) => {
      addAtCenter(option.type, option)
      setDialogOpen(false)
      // Optionally auto-expand minimap when closing dialog
      // (keeping minimized state for now - user can expand manually if needed)
    },
    [addAtCenter]
  )

  // Track selected nodes and edges for inspector
  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes, edges: selectedEdges }: { nodes: Node[]; edges: Edge[] }) => {
      const stickyNode = selectedNodes.find(n => n.type === 'sticky')
      const subFlowNode = selectedNodes.find(n => n.type === 'subflow')
      const triggerNode = selectedNodes.find(n => n.type === 'trigger')
      const actionNode = selectedNodes.find(n => n.type === 'action')

      setSelectedStickyNote(stickyNode || null)
      setSelectedSubFlow(subFlowNode || null)

      // Set selected node for properties panel (trigger or action)
      if (triggerNode) {
        setSelectedNode(triggerNode)
        setSelectedNodeType('trigger')
      } else if (actionNode) {
        setSelectedNode(actionNode)
        setSelectedNodeType('action')
      } else {
        setSelectedNode(null)
        setSelectedNodeType(null)
      }

      // Set selected edge for edge properties panel
      // Only show edge inspector when exactly one edge is selected and no nodes
      if (selectedEdges.length === 1 && selectedNodes.length === 0) {
        setSelectedEdge(selectedEdges[0])
      } else {
        setSelectedEdge(null)
      }
    },
    []
  )

  // Update sticky note data from inspector
  const handleInspectorChange = useCallback(
    (updates: Partial<any>) => {
      if (!selectedStickyNote) return

      // Update nodes array
      setNodes((nds) =>
        nds.map((n) =>
          n.id === selectedStickyNote.id
            ? { ...n, data: { ...n.data, ...updates } }
            : n
        )
      )

      // Also update selectedStickyNote so inspector shows latest data
      setSelectedStickyNote((prev) =>
        prev ? { ...prev, data: { ...prev.data, ...updates } } : null
      )
    },
    [selectedStickyNote, setNodes]
  )

  // Delete sticky note from inspector
  const handleDeleteStickyNote = useCallback(() => {
    if (!selectedStickyNote) return
    setNodes((nds) => nds.filter((n) => n.id !== selectedStickyNote.id))
    setSelectedStickyNote(null)
  }, [selectedStickyNote, setNodes])

  // Update subflow data from inspector
  const handleSubFlowInspectorChange = useCallback(
    (updates: Partial<any>) => {
      if (!selectedSubFlow) return

      setNodes((nds) =>
        nds.map((n) =>
          n.id === selectedSubFlow.id
            ? { ...n, data: { ...n.data, ...updates } }
            : n
        )
      )
    },
    [selectedSubFlow, setNodes]
  )

  // Update node properties from inspector (trigger/action nodes)
  const handleNodePropertiesChange = useCallback(
    (updates: Partial<any>) => {
      if (!selectedNode) return

      setNodes((nds) =>
        nds.map((n) =>
          n.id === selectedNode.id
            ? { ...n, data: { ...n.data, ...updates } }
            : n
        )
      )
    },
    [selectedNode, setNodes]
  )

  // Update edge properties from inspector
  const handleEdgePropertiesChange = useCallback(
    (updates: Partial<Edge>) => {
      if (!selectedEdge) return

      setEdges((eds) =>
        eds.map((e) =>
          e.id === selectedEdge.id
            ? { ...e, ...updates, data: { ...e.data, ...updates.data } }
            : e
        )
      )

      // Update selectedEdge state to reflect changes
      setSelectedEdge((prev) =>
        prev ? { ...prev, ...updates, data: { ...prev.data, ...updates.data } } : null
      )
    },
    [selectedEdge, setEdges]
  )

  // Delete selected edge
  const handleDeleteEdge = useCallback(() => {
    if (!selectedEdge) return
    setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id))
    setSelectedEdge(null)
  }, [selectedEdge, setEdges])

  // Reverse edge direction (swap source and target)
  const handleReverseEdge = useCallback(() => {
    if (!selectedEdge) return

    setEdges((eds) =>
      eds.map((e) =>
        e.id === selectedEdge.id
          ? {
              ...e,
              source: e.target,
              target: e.source,
              sourceHandle: e.targetHandle,
              targetHandle: e.sourceHandle,
            }
          : e
      )
    )

    // Update selected edge state
    setSelectedEdge((prev) =>
      prev
        ? {
            ...prev,
            source: prev.target,
            target: prev.source,
            sourceHandle: prev.targetHandle,
            targetHandle: prev.sourceHandle,
          }
        : null
    )
  }, [selectedEdge, setEdges])

  // Enrich nodes with callbacks for SubFlowNode and color cycler for all nodes
  const enrichedNodes = useMemo(() => {
    return nodes.map((node) => {
      // Action to cycle a node's color over the predefined palette
      const cycle = () => {
        setNodes((nds) => nds.map((n) => {
          if (n.id !== node.id) return n
          const current = (n.data?.color as NodeColor | undefined) ?? 'neutral'
          const idx = NODE_COLOR_OPTIONS.indexOf(current)
          const next = NODE_COLOR_OPTIONS[(idx + 1) % NODE_COLOR_OPTIONS.length]
          return { ...n, data: { ...n.data, color: next } }
        }))
      }

      if (node.type === 'subflow') {
        const childCount = getChildNodes(nodes, node.id).length
        return {
          ...node,
          className: [
            activeDropGroupId === node.id ? 'ring-2 ring-red-500/80' : '',
            (node.data as any)?.flashAttached ? 'flash-parent' : '',
          ].filter(Boolean).join(' '),
          data: {
            ...node.data,
            childCount,
            onToggleCollapse: () => subFlows.toggleCollapse(node.id),
            onExtractChildren: () => subFlows.extractChildren(node.id),
            onDeleteWithChildren: () => subFlows.deleteGroupWithChildren(node.id),
            onAutoResize: () => subFlows.autoResizeGroup(node.id),
            onCycleColor: cycle,
            // New Feature 7-9 callbacks
            onDuplicate: () => subFlows.duplicateGroup(node.id),
            onSelectAllChildren: () => {
              // Select all children of this group
              const childIds = subFlows.getChildNodeIds(node.id)
              setNodes((nds) =>
                nds.map((n) => ({
                  ...n,
                  selected: childIds.includes(n.id),
                }))
              )
            },
            onExportAsImage: () => subFlows.exportGroupAsImage(node.id),
            onApplyAutoLayout: (mode: import('@/hooks/useSubFlows').AutoLayoutMode) =>
              subFlows.applyAutoLayout(node.id, mode),
          },
        }
      }

      // Sticky notes: attach text/size change callbacks
      if (node.type === 'sticky') {
        return {
          ...node,
          data: {
            ...node.data,
            onTextChange: (text: string) => {
              const label = (text || '').trim().slice(0, 80) || 'Note'
              setNodes((nds) => nds.map((n) =>
                n.id === node.id ? { ...n, data: { ...n.data, text, label } } : n
              ))
            },
            onSizeChange: (size: { width: number; height: number }) => {
              setNodes((nds) => nds.map((n) =>
                n.id === node.id ? { ...n, data: { ...n.data, width: size.width, height: size.height } } : n
              ))
            },
            onEditingChange: (_isEditing: boolean) => {
              // Optional: track editing state if needed
            },
            onCycleColor: cycle,
            onDelete: () => {
              const pid = node.parentId
              setNodes((nds) => nds.filter((n) => n.id !== node.id))
              setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id))
              if (pid) {
                setTimeout(() => subFlows.autoResizeGroup(pid), 0)
              }
            },
          },
        }
      }

      // Other nodes: attach color cycle + detach/delete actions for parent-child relation UX
        return {
          ...node,
          data: {
            ...node.data,
            onCycleColor: cycle,
            onDetach: node.parentId ? () => subFlows.ungroupNode(node.id) : undefined,
            onDelete: () => {
              const pid = node.parentId
              setNodes((nds) => nds.filter((n) => n.id !== node.id))
              setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id))
              if (pid) {
                setTimeout(() => subFlows.autoResizeGroup(pid), 0)
              }
            },
          },
        }
    })
  }, [nodes, subFlows, activeDropGroupId, setNodes, setEdges])

  // Wrap onNodesChange with auto-resize logic
  const onNodesChangeWithAutoResize = useCallback(
    (changes: any[]) => {
      onNodesChange(changes)

      // Trigger auto-resize for affected subflows after a brief delay
      // This allows React Flow to process the changes first
      setTimeout(() => {
        const affectedParentIds = new Set<string>()

        // Check nodes array after changes to find which parents are affected
        changes.forEach((change) => {
          if (change.type === 'position' || change.type === 'remove' || change.type === 'add') {
            const node = nodes.find((n) => n.id === change.id)
            if (node?.parentId) {
              affectedParentIds.add(node.parentId)
            }
          }
        })

        // Auto-resize each affected parent
        affectedParentIds.forEach((parentId) => {
          subFlows.autoResizeGroup(parentId)
        })
      }, 50)
    },
    [onNodesChange, nodes, subFlows]
  )

  // Optionally wrap change handlers when devtools enabled
  const onNodesChangeWrapped = useMemo(
    () => (showDevtools ? withChangeLogger('NodesChange', onNodesChangeWithAutoResize) : onNodesChangeWithAutoResize),
    [showDevtools, onNodesChangeWithAutoResize]
  )
  const onEdgesChangeWrapped = useMemo(() => showDevtools ? withChangeLogger('EdgesChange', onEdgesChange) : onEdgesChange, [showDevtools, onEdgesChange])

  // Only render MiniMap when container is mounted and has size
  const canRenderMiniMap = useMemo(() => {
    if (!minimapReady) return false
    const el = containerRef.current as HTMLDivElement | null
    if (!el) return false
    if (el.clientWidth <= 0 || el.clientHeight <= 0) return false
    // Avoid over-restrictive checks; MiniMap can render before nodes report width/height
    return true
  }, [minimapReady, containerRef])
  // Broadcast viewport movement to the minimap auto-hide logic
  const broadcastViewportMove = useCallback(() => {
    window.dispatchEvent(new Event('minimap:viewport-move'))
    updateZoomPercent()
  }, [updateZoomPercent])

  // Stable callbacks for CanvasActions to prevent infinite re-renders with Radix UI Tooltip
  const handleAddNode = useCallback(() => {
    setDialogOpen(true)
    // Auto-minimize minimap when opening Add Node dialog
    if (minimapVisible && !minimapMinimized) {
      setMinimapMinimized(true)
      localStorage.setItem('canvas:minimap-minimized', 'true')
    }
  }, [minimapVisible, minimapMinimized])

  const handleAddStickyNote = useCallback(() => {
    addAtCenter('sticky')
  }, [addAtCenter])

  return (
    <div
      className="flex-1 relative"
      ref={containerRef as any}
      onDragOver={(e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
      }}
      onDrop={(e) => {
        setActiveDropGroupId(null)
        onDrop(e)
      }}
    >
      <ReactFlow
        nodes={enrichedNodes}
        edges={edges}
  onNodesChange={onNodesChangeWrapped}
  onEdgesChange={onEdgesChangeWrapped}
        onConnect={onConnect}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onSelectionChange={handleSelectionChange}
  onMove={broadcastViewportMove}
  onMoveStart={broadcastViewportMove}
  onMoveEnd={broadcastViewportMove}
  onInit={updateZoomPercent}
        connectionMode={ConnectionMode.Strict}
        nodeTypes={nodeTypes}
        defaultViewport={DEFAULT_VIEWPORT}
        edgesFocusable
        elementsSelectable
        snapToGrid={showGrid}
        snapGrid={[16, 16]}
        panOnDrag={spacePan}
        selectionOnDrag={!spacePan}
        panOnScroll
        zoomOnPinch
      >
  {showGrid && canvasReady && <BackgroundPattern variant={bgVariant} />}
        <HelperLines lines={helperLines.helperLines} />
        <WorkflowActions
          onSave={handleQuickSave}
          onSaveAs={handleSaveAs}
          onNew={handleNewWorkflow}
          onLoad={handleLoadWorkflow}
          onExport={handleExportWorkflow}
          // Use pendingName for n8n-style: allows editing name before saving
          workflowName={pendingName}
          workflowId={workflow.currentWorkflow?.id}
          isSaving={workflow.isSaving}
          // Check for unsaved changes: workflow changes OR pending name/tags differ from saved
          hasUnsavedChanges={
            workflow.hasUnsavedChanges ||
            pendingTags.length !== (workflow.currentWorkflow?.tags?.length || 0) ||
            pendingName !== (workflow.currentWorkflow?.name || 'Nuevo Flujo Visual')
          }
          lastSaved={workflow.lastSaved}
          // n8n-style inline editing: always available (works before saving!)
          onNameChange={handleNameChange}
          // Use pendingTags for n8n-style: allows adding tags before saving
          workflowTags={pendingTags}
          availableTags={availableTags}
          tagsLoading={tagsLoading}
          // Tag handlers always available (works before saving!)
          onTagAdd={handleTagAdd}
          onTagRemove={handleTagRemove}
          onTagCreate={handleTagCreate}
        />
        <CanvasActions onAddNode={handleAddNode} onAddStickyNote={handleAddStickyNote} onEditTags={handleSaveAs} />

        {/* PROJ-082: Privacy governance compliance toolbar */}
        {workflow.currentWorkflow?.id && (
          <Panel position="top-right" className="z-10">
            <ComplianceToolbar flowId={workflow.currentWorkflow.id} />
          </Panel>
        )}

        {/* Bottom-left: Unified navigation controls (zoom, pan, minimap) */}
        <Panel position="bottom-left" className="z-10">
          <div className="flex items-center gap-1 px-2 py-1.5 bg-background/95 border border-border rounded-lg shadow-sm">
            {/* Zoom percentage */}
            <span className="px-2 py-0.5 text-xs font-semibold tabular-nums min-w-[42px] text-center">
              {zoomPercent}%
            </span>

            <div className="w-px h-5 bg-border mx-1" />

            {/* Zoom In */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              className="h-7 w-7 p-0"
              aria-label="Acercar zoom"
              title="Acercar (⌘+)"
            >
              <Plus className="h-4 w-4" />
            </Button>

            {/* Zoom Out */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              className="h-7 w-7 p-0"
              aria-label="Alejar zoom"
              title="Alejar (⌘-)"
            >
              <Minus className="h-4 w-4" />
            </Button>

            <div className="w-px h-5 bg-border mx-1" />

            {/* Pan Mode */}
            <Button
              variant={spacePan ? 'default' : 'ghost'}
              size="sm"
              onClick={handleTogglePan}
              className="h-7 w-7 p-0"
              aria-label={spacePan ? 'Desactivar modo pan' : 'Activar modo pan'}
              title={spacePan ? 'Desactivar Pan (Espacio)' : 'Activar Pan (Espacio)'}
            >
              <Hand className="h-4 w-4" />
            </Button>

            {/* Minimap Toggle */}
            <Button
              variant={minimapVisible ? 'default' : 'ghost'}
              size="sm"
              onClick={handleToggleMinimap}
              className="h-7 w-7 p-0"
              aria-label={minimapVisible ? 'Ocultar mini-mapa' : 'Mostrar mini-mapa'}
              title={minimapVisible ? 'Ocultar Mini-Mapa' : 'Mostrar Mini-Mapa'}
            >
              <Map className="h-4 w-4" />
            </Button>
          </div>
        </Panel>

        {/* Bottom-right: Canvas debug controls (Trace toggle + z-index) */}
        <Panel position="bottom-right" className="z-10">
          <div className="flex items-center gap-3 px-3 py-1.5 text-xs bg-background/90 border border-border rounded shadow-sm">
            <div className="font-mono text-muted-foreground">z-index: 10</div>
            <Toggle checked={showTrace} onChange={setShowTrace} label="Trace" />
          </div>
        </Panel>

        {/* MiniMap: auto-ocultable + glass modern style; render only when safe */}
        {canRenderMiniMap && (
          <AutoHideMiniMap
            visible={minimapVisible}
            minimized={minimapMinimized}
            onToggleMinimized={handleToggleMinimapMinimized}
          />
        )}

        {showDevtools && (() => {
          const el = (containerRef as any).current as HTMLDivElement | null
          const bounds = el ? { width: el.clientWidth, height: el.clientHeight } : undefined
          return (
            <>
              <FloatingPanel
                x={16}
                y={16}
                bounds={bounds}
                title="Viewport"
                sourceFile="components/devtools/ViewportLogger.tsx"
                zIndexClass="z-50"
                getContainerRect={() => {
                  const r = el?.getBoundingClientRect();
                  return { left: r?.left || 0, top: r?.top || 0 };
                }}
              >
                <div className="w-[160px]" onMouseDown={(e) => e.stopPropagation()}>
                  <ViewportLogger />
                </div>
              </FloatingPanel>
              <FloatingPanel
                x={16}
                y={110}
                bounds={bounds}
                title="Node Inspector"
                sourceFile="components/devtools/NodeInspector.tsx"
                zIndexClass="z-50"
                getContainerRect={() => {
                  const r = el?.getBoundingClientRect();
                  return { left: r?.left || 0, top: r?.top || 0 };
                }}
              >
                <div className="w-[260px]" onMouseDown={(e) => e.stopPropagation()}>
                  <NodeInspector />
                </div>
              </FloatingPanel>
            </>
          )
        })()}
        {showDevtools && (
          <>
            {!perfVisible && (
              <div className="fixed bottom-4 right-4 z-[9999]">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPerfVisible(true)}
                  className="bg-yellow-100 border-yellow-400 text-yellow-900 hover:bg-yellow-200 shadow-lg"
                  title="DevTools (Shift+D)"
                >
                  <Activity className="h-4 w-4" />
                </Button>
              </div>
            )}
            {perfVisible && (() => {
              const el = (containerRef as any).current as HTMLDivElement | null
              const bounds = el ? { width: el.clientWidth, height: el.clientHeight } : undefined
              const panelWidth = 320
              const initialX = bounds ? Math.max(12, bounds.width - panelWidth - 16) : 24
              const initialY = bounds ? Math.max(12, bounds.height - 260) : 24
              return (
                <FloatingPanel
                  x={initialX}
                  y={initialY}
                  bounds={bounds}
                  title="DevTools"
                  sourceFile="components/CanvasPane.tsx"
                  zIndexClass="z-50"
                  onClose={() => setPerfVisible(false)}
                  getContainerRect={() => {
                    const r = el?.getBoundingClientRect();
                    return { left: r?.left || 0, top: r?.top || 0 };
                  }}
                >
                  <div className="w-[320px] bg-slate-900 text-white rounded-lg shadow-2xl border border-slate-700 overflow-hidden" onMouseDown={(e)=>e.stopPropagation()} onWheelCapture={(e)=>e.stopPropagation()}>

                <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-yellow-400" />
                    <span className="font-semibold text-sm">DevTools</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPerfVisible(false)}
                    className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="p-4 space-y-3 text-sm font-mono">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Database className="h-3.5 w-3.5" />
                      <span>Nodes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">{nodes.length}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Edges</span>
                    <span className="px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded">{edges.length}</span>
                  </div>
                  <div className="border-t border-slate-700 pt-3" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Activity className="h-3.5 w-3.5" />
                      <span>FPS</span>
                    </div>
                    <span className={`px-1.5 py-0.5 text-xs rounded border ${
                      fps >= 50
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : fps >= 30
                        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                      {fps}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Render Time</span>
                    </div>
                    <span className={`px-1.5 py-0.5 text-xs rounded border ${
                      renderTime < 16
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : renderTime < 50
                        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                      {renderTime.toFixed(2)}ms
                    </span>
                  </div>
                  {memoryUsage !== null && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Cpu className="h-3.5 w-3.5" />
                        <span>Memory</span>
                      </div>
                      <span className="px-1.5 py-0.5 text-xs bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 rounded">
                        {memoryUsage.toFixed(1)} MB
                      </span>
                    </div>
                  )}
                </div>
                <div className="px-4 py-2 bg-slate-800 border-t border-slate-700 text-xs text-slate-400">
                  Press <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-yellow-400">Shift+D</kbd> to toggle
                </div>
                  </div>
                </FloatingPanel>
              )})()}
          </>
        )}
      </ReactFlow>

      {nodes.length === 0 && <EmptyStateCTA onClick={() => setDialogOpen(true)} />}

      {dialogOpen && (() => {
        const el = (containerRef as any).current as HTMLDivElement | null
        const bounds = el ? { width: el.clientWidth, height: el.clientHeight } : undefined
        const initialX = bounds ? Math.max(12, Math.floor((bounds.width - 312) / 2)) : 24
        const initialY = 48
        return (
          <FloatingPanel
            x={initialX}
            y={initialY}
            bounds={bounds}
            title="Agregar al Flujo Visual"
            sourceFile="components/dialogs/AddNodePanelContent.tsx"
            onClose={() => setDialogOpen(false)}
            zIndexClass="z-50"
            getContainerRect={() => {
              const r = el?.getBoundingClientRect()
              return { left: r?.left || 0, top: r?.top || 0 }
            }}
          >
            <AddNodePanelContent onClose={() => setDialogOpen(false)} onSelect={handleSelectFromDialog} />
          </FloatingPanel>
        )
      })()}

      {/* Sticky Note Inspector - Draggable inside canvas */}
      {selectedStickyNote && selectedStickyNote.type === 'sticky' && (
        (() => {
          const el = (containerRef as any).current as HTMLDivElement | null
          const bounds = el
            ? { width: el.clientWidth, height: el.clientHeight }
            : undefined
          const initialX = bounds ? Math.max(12, bounds.width - 240 - 12) : 24
          const initialY = 24
          return (
            <FloatingPanel
              x={initialX}
              y={initialY}
              bounds={bounds}
              title="Propiedades de Nota"
              sourceFile="components/inspectors/StickyNoteInspector.tsx"
              zIndexClass="z-40"
              onClose={() => setSelectedStickyNote(null)}
              getContainerRect={() => {
                const el = (containerRef as any).current as HTMLDivElement | null
                const r = el?.getBoundingClientRect()
                return { left: r?.left || 0, top: r?.top || 0 }
              }}
            >
              {({ onDragStart, showTrace }) => (
                <div
                  className={`w-[280px] min-h-0 max-h-[70vh] overflow-y-auto rounded-md ${
                    showTrace
                      ? 'bg-background border border-border'
                      : 'bg-background/95 shadow-lg'
                  }`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onWheelCapture={(e) => e.stopPropagation()}
                >
                  <StickyNoteInspector
                    data={selectedStickyNote.data as unknown as StickyNoteData}
                    onChange={handleInspectorChange}
                    onDelete={handleDeleteStickyNote}
                    currentNode={selectedStickyNote}
                    allNodes={nodes}
                    onDragStart={onDragStart}
                  />
                </div>
              )}
            </FloatingPanel>
          )
        })()
      )}

      {/* SubFlow Inspector - Draggable inside canvas */}
      {selectedSubFlow && selectedSubFlow.type === 'subflow' && (
        (() => {
          const el = (containerRef as any).current as HTMLDivElement | null
          const bounds = el
            ? { width: el.clientWidth, height: el.clientHeight }
            : undefined
          const initialX = bounds ? Math.max(12, bounds.width - 240 - 12) : 24
          const initialY = selectedStickyNote ? 80 : 24 // Offset if sticky note inspector is open

          // Get enriched node data
          const enrichedNode = enrichedNodes.find(n => n.id === selectedSubFlow.id)

          return (
            <FloatingPanel
              x={initialX}
              y={initialY}
              bounds={bounds}
              title="Propiedades del Grupo"
              sourceFile="components/inspectors/SubFlowInspector.tsx"
              zIndexClass="z-40"
              onClose={() => setSelectedSubFlow(null)}
              getContainerRect={() => {
                const el = (containerRef as any).current as HTMLDivElement | null
                const r = el?.getBoundingClientRect()
                return { left: r?.left || 0, top: r?.top || 0 }
              }}
            >
              {({ onDragStart, showTrace }) => (
                <div
                  className={`w-[240px] min-h-0 max-h-[60vh] overflow-y-auto rounded-md ${
                    showTrace
                      ? 'bg-background border border-border'
                      : 'bg-background/95 shadow-lg'
                  }`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onWheelCapture={(e) => e.stopPropagation()}
                >
                  <SubFlowInspector
                    data={(enrichedNode?.data || selectedSubFlow.data) as any}
                    onChange={handleSubFlowInspectorChange}
                    onToggleCollapse={(enrichedNode?.data as any)?.onToggleCollapse as (() => void) | undefined}
                    onAutoResize={(enrichedNode?.data as any)?.onAutoResize as (() => void) | undefined}
                    onUngroup={(enrichedNode?.data as any)?.onExtractChildren as (() => void) | undefined}
                    onDelete={(enrichedNode?.data as any)?.onDeleteWithChildren as (() => void) | undefined}
                    onDuplicate={(enrichedNode?.data as any)?.onDuplicate as (() => void) | undefined}
                    onSelectAllChildren={(enrichedNode?.data as any)?.onSelectAllChildren as (() => void) | undefined}
                    onExportAsImage={(enrichedNode?.data as any)?.onExportAsImage as (() => void) | undefined}
                    onApplyAutoLayout={(enrichedNode?.data as any)?.onApplyAutoLayout}
                    onDragStart={onDragStart}
                  />
                </div>
              )}
            </FloatingPanel>
          )
        })()
      )}

      {/* Node Properties Panel - For Trigger and Action nodes */}
      {selectedNode && selectedNodeType && (selectedNodeType === 'trigger' || selectedNodeType === 'action') && (
        (() => {
          const el = (containerRef as any).current as HTMLDivElement | null
          const bounds = el
            ? { width: el.clientWidth, height: el.clientHeight }
            : undefined
          const initialX = bounds ? Math.max(12, bounds.width - 280 - 12) : 24
          // Stack vertically if other inspectors are open
          let initialY = 24
          if (selectedStickyNote) initialY += 60
          if (selectedSubFlow) initialY += 60

          return (
            <FloatingPanel
              x={initialX}
              y={initialY}
              bounds={bounds}
              title={`${selectedNodeType === 'trigger' ? 'Trigger' : 'Action'} Properties`}
              sourceFile="components/inspectors/NodePropertiesPanel.tsx"
              zIndexClass="z-40"
              onClose={() => {
                setSelectedNode(null)
                setSelectedNodeType(null)
              }}
              getContainerRect={() => {
                const el = (containerRef as any).current as HTMLDivElement | null
                const r = el?.getBoundingClientRect()
                return { left: r?.left || 0, top: r?.top || 0 }
              }}
            >
              {({ onDragStart, showTrace }) => (
                <div
                  className={`w-[280px] min-h-0 max-h-[70vh] overflow-y-auto rounded-md ${
                    showTrace
                      ? 'bg-background border border-border'
                      : 'bg-background/95 shadow-lg'
                  }`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onWheelCapture={(e) => e.stopPropagation()}
                >
                  <NodePropertiesPanel
                    nodeId={selectedNode.id}
                    data={selectedNode.data}
                    onChange={handleNodePropertiesChange}
                    nodeType={selectedNodeType}
                    onDragStart={onDragStart}
                  />
                </div>
              )}
            </FloatingPanel>
          )
        })()
      )}

      {/* Edge Properties Panel - For selected edges */}
      {selectedEdge && (
        (() => {
          const el = (containerRef as any).current as HTMLDivElement | null
          const bounds = el
            ? { width: el.clientWidth, height: el.clientHeight }
            : undefined
          const initialX = bounds ? Math.max(12, bounds.width - 280 - 12) : 24
          // Stack vertically if other inspectors are open
          let initialY = 24
          if (selectedStickyNote) initialY += 60
          if (selectedSubFlow) initialY += 60
          if (selectedNode) initialY += 60

          return (
            <FloatingPanel
              x={initialX}
              y={initialY}
              bounds={bounds}
              title="Propiedades del Edge"
              sourceFile="components/inspectors/EdgePropertiesPanel.tsx"
              zIndexClass="z-40"
              onClose={() => setSelectedEdge(null)}
              getContainerRect={() => {
                const el = (containerRef as any).current as HTMLDivElement | null
                const r = el?.getBoundingClientRect()
                return { left: r?.left || 0, top: r?.top || 0 }
              }}
            >
              {({ onDragStart, showTrace }) => (
                <div
                  className={`w-[280px] min-h-0 max-h-[70vh] overflow-y-auto rounded-md ${
                    showTrace
                      ? 'bg-background border border-border'
                      : 'bg-background/95 shadow-lg'
                  }`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onWheelCapture={(e) => e.stopPropagation()}
                >
                  <EdgePropertiesPanel
                    edge={selectedEdge}
                    nodes={nodes}
                    onChange={handleEdgePropertiesChange}
                    onDelete={handleDeleteEdge}
                    onReverse={handleReverseEdge}
                    onDragStart={onDragStart}
                  />
                </div>
              )}
            </FloatingPanel>
          )
        })()
      )}

      {/* Workflow Save Dialog */}
      <WorkflowSaveDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={handleSaveDialogSubmit}
        initialData={workflow.currentWorkflow ? {
          // Use pendingName (n8n-style: includes name edited before saving)
          name: pendingName,
          description: workflow.currentWorkflow.description,
          status: workflow.currentWorkflow.status as 'draft' | 'active' | 'archived',
          visibility: workflow.currentWorkflow.visibility as 'private' | 'team' | 'organization',
          // Use pendingTags (n8n-style: includes tags added before saving)
          tags: pendingTags.map((tag) => tag.name),
        } : {
          // For new workflows: pre-fill with pending name and tags
          name: pendingName,
          tags: pendingTags.map((tag) => tag.name),
        }}
        mode={workflow.currentWorkflow ? 'update' : 'create'}
      />

      {/* Workflow Load Dialog */}
      <WorkflowLoadDialog
        open={loadDialogOpen}
        onOpenChange={setLoadDialogOpen}
        onLoad={handleLoadDialogSubmit}
        onListWorkflows={workflow.listWorkflows}
      />
    </div>
  )
}
